import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getAppSocket } from '../lib/wesSocket';
import { ArrowLeftIcon, ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';

interface Movement {
  id: string;
  accountId: string;
  type: 'charge' | 'payment' | 'credit' | 'debit' | 'adjustment';
  amount: number;
  description: string;
  reference?: string;
  invoiceId?: string;
  budgetId?: string;
  paymentMethod?: string;
  previousBalance: number;
  newBalance: number;
  createdBy?: string;
  createdByName?: string;
  createdByLastName?: string;
  createdAt: string;
}

interface AccountInfo {
  id: string;
  clientId?: string;
  entityId: string;
  currentBalance: number;
  creditLimit: number;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  displayName?: string;
  phone?: string;
  email?: string;
}

const CashRegisterDetail: React.FC = () => {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const [movements, setMovements] = useState<Movement[]>([]);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (accountId) {
      fetchAccountInfo();
      fetchMovements();
    }
  }, [accountId]);

  const realtimeTimerRef = useRef<number | null>(null);
  useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (path && !(path.startsWith('/api/current-accounts') || path.startsWith('/api/remitos') || path.startsWith('/api/invoices') || path.startsWith('/api/checks'))) {
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchAccountInfo();
        fetchMovements();
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [accountId]);

  const fetchAccountInfo = async () => {
    if (!accountId) return;
    
    try {
      // Obtener todas las cuentas y buscar la que coincide con accountId
      const allAccountsResponse = await axios.get('/api/current-accounts?entityType=client');
      const account = allAccountsResponse.data.accounts?.find((acc: any) => acc.id === accountId);
      if (account) {
        setAccountInfo(account);
      } else {
        toast.error('No se encontró la cuenta del cliente');
      }
    } catch (error) {
      console.error('Error fetching account info:', error);
      toast.error('Error al cargar la información de la cuenta');
    }
  };

  const fetchMovements = async () => {
    if (!accountId) return;
    
    try {
      const response = await axios.get(`/api/current-accounts/${accountId}/movements`);
      const movementsData = response.data.movements || [];
      // Ordenar por fecha más reciente primero
      movementsData.sort((a: Movement, b: Movement) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setMovements(movementsData);
    } catch (error) {
      console.error('Error fetching movements:', error);
      toast.error('Error al cargar los movimientos');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMovementTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      charge: 'Cargo',
      payment: 'Pago',
      credit: 'Crédito',
      debit: 'Débito',
      adjustment: 'Ajuste',
    };
    return labels[type] || type;
  };

  const getMovementIcon = (type: string) => {
    if (type === 'payment' || type === 'credit') {
      return <ArrowDownIcon className="h-4 w-4 text-green-500" />;
    }
    return <ArrowUpIcon className="h-4 w-4 text-red-500" />;
  };

  const getPaymentMethodLabel = (method?: string) => {
    if (!method) return null;
    const labels: Record<string, string> = {
      efectivo: 'Efectivo',
      debito: 'Débito',
      transferencia: 'Transferencia',
      tarjeta_3: 'Tarjeta 3 cuotas',
      tarjeta_6: 'Tarjeta 6 cuotas',
      cuenta_corriente: 'Cuenta Corriente',
      cheque: 'Cheque',
    };
    return labels[method] || method;
  };

  const getClientName = () => {
    if (!accountInfo) return 'Cliente';
    return accountInfo.displayName || accountInfo.companyName || `${accountInfo.firstName || ''} ${accountInfo.lastName || ''}`.trim() || 'Cliente sin nombre';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con botón de volver */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate('/cash-register')}
          className="flex items-center gap-2"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Volver
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">
          Movimientos - {getClientName()}
        </h1>
      </div>

      {/* Información de la cuenta */}
      {accountInfo && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Saldo Actual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(accountInfo.currentBalance)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Límite de Crédito
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-muted-foreground">
                {accountInfo.creditLimit > 0 ? formatCurrency(accountInfo.creditLimit) : '-'}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Disponible
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">
                {accountInfo.creditLimit > 0 
                  ? formatCurrency(Math.max(0, accountInfo.creditLimit - accountInfo.currentBalance))
                  : '-'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabla de movimientos */}
      <Card>
        <CardHeader>
          <CardTitle>Historial de Movimientos</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {movements.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No hay movimientos registrados para este cliente.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[10%]">Tipo</TableHead>
                    <TableHead className="w-[15%]">Fecha</TableHead>
                    <TableHead className="w-[25%]">Descripción</TableHead>
                    <TableHead className="w-[12%]">Método de Pago</TableHead>
                    <TableHead className="w-[12%] text-right">Monto</TableHead>
                    <TableHead className="w-[12%] text-right">Saldo Anterior</TableHead>
                    <TableHead className="w-[12%] text-right">Saldo Nuevo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((movement) => (
                    <TableRow key={movement.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getMovementIcon(movement.type)}
                          <Badge variant="outline">
                            {getMovementTypeLabel(movement.type)}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(movement.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col">
                          <span>{movement.description}</span>
                          {movement.reference && (
                            <span className="text-xs text-muted-foreground">
                              Ref: {movement.reference}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {movement.paymentMethod ? (
                          <Badge variant="secondary">
                            {getPaymentMethodLabel(movement.paymentMethod)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className={`text-right font-semibold ${
                        movement.type === 'payment' || movement.type === 'credit'
                          ? 'text-green-600'
                          : 'text-red-600'
                      }`}>
                        {movement.type === 'payment' || movement.type === 'credit' ? '+' : '-'}
                        {formatCurrency(movement.amount)}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(movement.previousBalance)}
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(movement.newBalance)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CashRegisterDetail;

